/**
 * @file Rendu des projectiles : balles/plombs/grenades/missiles en InstancedMesh (une forme = un InstancedMesh + sa coque de contour,
 *       couleurs principale/secondaire par instance), halos lumineux (quads face camera), faisceaux laser en quads pooles.
 */
import {
  Color,
  CylinderGeometry,
  DataTexture,
  DynamicDrawUsage,
  Euler,
  InstancedBufferAttribute,
  InstancedMesh,
  LinearFilter,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Quaternion,
  RGBAFormat,
  Vector3,
} from 'three';
import type { BufferGeometry, Material, Scene } from 'three';
import { CONFIG, RENDER } from '@config/index';
import type { AmmoShape, AmmoStyle } from '@config/types';
import { WEAPON_BEHAVIORS } from '@shared/enums';
import type { WeaponBehavior } from '@shared/enums';
import { buildProjectileShape, buildRocket } from '../ammo/AmmoShapes';
import type { ShapeParts } from '../ammo/AmmoShapes';
import { colorOf, rainbowInto } from '../ammo/colors';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ToonKit } from '../toon/toon';

export interface ProjectileFrame {
  behavior: WeaponBehavior;
  kind: 'bullet' | 'pellet' | 'grenade' | 'missile' | 'beam';
  x: number;
  y: number;
  rot: number;
  /** Missile : progression 0..1 de l'arc et hauteur max (z visuel = 4*h*t*(1-t) + 0.6). */
  arcT: number;
  arcHeight: number;
  /** Faisceau : extremite et largeur ; opacite = lifeLeft / beamVisibleSeconds. */
  x1: number;
  y1: number;
  width: number;
  fade: number;
  crit: boolean;
}

/** Emet une particule de traine (coordonnees Three ; dirX/dirZ = direction de vol horizontale unitaire). */
export type TrailSink = (style: AmmoStyle, x: number, y: number, z: number, dirX: number, dirZ: number) => void;

/** Hauteur de vol (unites Three) des balles / faisceaux au-dessus du sol. */
const FLIGHT_HEIGHT = 0.6;
const BEAM_POOL = 16;
/** Pente horizontale approximative d'un arc de missile (portee moyenne / 2) pour incliner le nez selon la tangente. */
const MISSILE_RUN = 8;
const CRIT_COLOR = '#ffffff';
/** Formes plates (orientees face camera, cap = direction de vol a l'ecran) ; les autres sont orientees dans le monde. */
const BILLBOARD: Record<AmmoShape, boolean> = { capsule: false, orb: false, diamond: false, star: true, heart: true, fish: true };
/** Echelle de reference par forme (les formes plates sont un peu plus larges que l'orbe de 0.12). */
const SHAPE_SCALE: Record<AmmoShape, number> = { capsule: 1, orb: 1, diamond: 1, star: 0.95, heart: 0.9, fish: 1 };
/** Emission d'une particule de traine toutes les N frames par projectile (echantillonnage), et budget par frame. */
const TRAIL_PERIOD = { bullet: 5, pellet: 9, grenade: 3, missile: 2 } as const;
const TRAIL_BUDGET = 10;
const TRAIL_BUDGET_ECO = 4;
const GLOW_BEHIND = 0.3;
const AXIS_Z = new Vector3(0, 0, 1);
/** Rotation de PI autour de X : retourne le poisson quand il va vers la gauche (dos toujours vers le haut de l'ecran). */
const FLIP_X = new Quaternion(1, 0, 0, 0);

interface Pool {
  mesh: InstancedMesh;
  hull: InstancedMesh | null;
  /** Teinte secondaire par instance (attribut d'instance de la geometrie ; null pour le halo). */
  tint: InstancedBufferAttribute | null;
  n: number;
}

interface BeamSlot {
  /** Halo colore (opaque a l'avant-plan, fondu par `fade`) et coeur plus fin. */
  mesh: Mesh;
  material: MeshBasicMaterial;
  rainbow: MeshBasicMaterial;
  rainbowUniforms: { uLen: { value: number }; uTime: { value: number } };
  core: Mesh;
  coreMaterial: MeshBasicMaterial;
}

const RAINBOW_VERT_DECL = 'varying float vBeamT;\n';
const RAINBOW_FRAG_DECL = /* glsl */ `
uniform float uLen;
uniform float uTime;
varying float vBeamT;
vec3 beamHue(float h) { return clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0); }
`;

/**
 * Specification visuelle (style de la categorie d'arme, lu a la volee via `ammoStyleOf`) :
 *  - bullet (single/pierce) : forme du skin (capsule etiree = ancien look), crit = plus grosse + blanche ; pierce : plus longue.
 *  - pellet : petite forme.  grenade : grosse forme.  missile : fusee bicolore (capsule/orb) ou grosse forme du skin, avec arc.
 *  - beam (laser/blastSniper) : cylindre fin pool de 16 ; halo colors[0], coeur colors[1], arc-en-ciel = degrade qui defile.
 *  - glow : quad face camera a degrade radial (colors[0]), derriere le projectile.   rainbow : teinte qui cycle dans le temps.
 *  - Traine : delegue au FxManager via `trailSink` (echantillonnee, budget par frame).
 *  - Contour : coque inverted-hull par forme (kit.outline) ; pas pour les faisceaux ni les halos.
 */
export class ProjectileRenderer {
  /** Rempli par GameRenderer : emet une particule de traine selon `style.trail`. */
  trailSink: TrailSink | null = null;
  /** Vrai si un FX de tir equipe veut une traine meme quand le skin de munition n'en a pas. */
  forceTrail = false;

  private readonly shapes: Record<AmmoShape, Pool>;
  private readonly rocket: Pool;
  private readonly glow: Pool;
  private readonly pools: Pool[];
  private readonly beams: BeamSlot[] = [];
  private beamsUsed = 0;
  private readonly styles: Record<WeaponBehavior, AmmoStyle | null> = { single: null, pierce: null, shotgun: null, explosive: null, laser: null, missile: null, blastSniper: null };
  private readonly cMain = new Color();
  private readonly cAcc = new Color();
  private readonly matrix = new Matrix4();
  private readonly euler = new Euler();
  private readonly quat = new Quaternion();
  private readonly roll = new Quaternion();
  private readonly camQ = new Quaternion();
  private readonly right = new Vector3();
  private readonly up = new Vector3();
  private readonly fwd = new Vector3();
  private readonly pos = new Vector3();
  private readonly scl = new Vector3();
  private readonly dir = new Vector3();
  private tick = 0;
  private time = 0;
  private drawIndex = 0;
  private budget = TRAIL_BUDGET;
  private eco = false;

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
    private readonly ammoStyleOf: (behavior: WeaponBehavior) => AmmoStyle,
  ) {
    const cap = CONFIG.sim.limits.projectiles;
    const material = kit.ammoToon();
    const make = (key: string, build: () => ShapeParts): Pool => {
      let hullSrc: BufferGeometry | null = null;
      const geometry = geo.get(key, () => {
        const parts = build();
        hullSrc = parts.hull;
        return parts.fill;
      });
      const pool = this.makePool(geometry, material, cap, hullSrc);
      hullSrc = (hullSrc as BufferGeometry | null) ?? null;
      if (hullSrc) (hullSrc as BufferGeometry).dispose();
      return pool;
    };
    const shape = (s: AmmoShape): Pool => make(`ammo:${s}`, () => buildProjectileShape(s));
    this.shapes = { capsule: shape('capsule'), orb: shape('orb'), diamond: shape('diamond'), star: shape('star'), heart: shape('heart'), fish: shape('fish') };
    this.rocket = make('ammo:rocket', buildRocket);

    // halo lumineux : quad face camera a degrade radial
    const tex = kit.track(this.glowTexture());
    const glowMat = kit.track(new MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
    const quad = geo.get('ammo:glowQuad', () => new PlaneGeometry(1, 1));
    const glowMesh = new InstancedMesh(quad, glowMat, cap);
    glowMesh.instanceMatrix.setUsage(DynamicDrawUsage);
    glowMesh.instanceColor = new InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    glowMesh.instanceColor.setUsage(DynamicDrawUsage);
    glowMesh.frustumCulled = false;
    glowMesh.count = 0;
    glowMesh.visible = false;
    glowMesh.renderOrder = 1;
    scene.add(glowMesh);
    this.glow = { mesh: glowMesh, hull: null, tint: null, n: 0 };

    this.pools = [...Object.values(this.shapes), this.rocket, this.glow];

    const beamGeo = geo.get('proj:beam', () => {
      const g = new CylinderGeometry(0.5, 0.5, 1, 8, 1, true);
      g.rotateX(Math.PI / 2);
      return g;
    });
    for (let i = 0; i < BEAM_POOL; i++) {
      const bm = kit.track(new MeshBasicMaterial({ color: '#7ff3ff', transparent: true, opacity: 0, depthWrite: false }));
      const coreMaterial = kit.track(new MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0, depthWrite: false }));
      const rainbowUniforms = { uLen: { value: 1 }, uTime: { value: 0 } };
      const rainbow = kit.track(new MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0, depthWrite: false }));
      rainbow.onBeforeCompile = (shader) => {
        shader.uniforms.uLen = rainbowUniforms.uLen;
        shader.uniforms.uTime = rainbowUniforms.uTime;
        shader.vertexShader = RAINBOW_VERT_DECL + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vBeamT = position.z + 0.5;');
        shader.fragmentShader = RAINBOW_FRAG_DECL + shader.fragmentShader.replace('#include <color_fragment>', '#include <color_fragment>\n  diffuseColor.rgb *= mix(vec3(1.0), beamHue(vBeamT * uLen * 0.14 - uTime * 0.7), 0.72);');
      };
      rainbow.customProgramCacheKey = () => 'beamRainbow';
      const mesh = new Mesh(beamGeo, bm);
      const core = new Mesh(beamGeo, coreMaterial);
      for (const m of [mesh, core]) {
        m.frustumCulled = false;
        m.visible = false;
        m.renderOrder = 3;
        scene.add(m);
      }
      this.beams.push({ mesh, material: bm, rainbow, rainbowUniforms, core, coreMaterial });
    }
  }

  /** Orientation de la camera (faces camera : formes plates, halos). Objet lu a chaque begin(). */
  setCamera(q: Quaternion): void {
    this.camQ.copy(q);
  }

  setEco(eco: boolean): void {
    this.eco = eco;
  }

  begin(time: number): void {
    for (const pool of this.pools) pool.n = 0;
    this.beamsUsed = 0;
    this.tick++;
    this.time = time;
    this.drawIndex = 0;
    this.budget = this.eco ? TRAIL_BUDGET_ECO : TRAIL_BUDGET;
    for (const b of WEAPON_BEHAVIORS) this.styles[b] = this.ammoStyleOf(b);
    this.right.set(1, 0, 0).applyQuaternion(this.camQ);
    this.up.set(0, 1, 0).applyQuaternion(this.camQ);
    this.fwd.set(0, 0, -1).applyQuaternion(this.camQ);
  }

  draw(p: ProjectileFrame): void {
    const style = this.styles[p.behavior];
    if (!style) return;
    if (p.kind === 'beam') {
      this.drawBeam(p, style);
      return;
    }
    const idx = this.drawIndex++;
    this.colorsOf(style, p.crit, idx);
    const size = style.size;
    let h = FLIGHT_HEIGHT;
    let glowK = 0.75;
    switch (p.kind) {
      case 'bullet': {
        const pierce = p.behavior === 'pierce';
        const k = (p.crit ? 1.5 : 1) * size;
        this.putShape(style.shape, p.x, h, p.y, p.rot, 0, k, pierce ? 1.6 : 1, idx);
        break;
      }
      case 'pellet': {
        const k = (p.crit ? 0.95 : 0.7) * size * (BILLBOARD[style.shape] ? 0.82 : 1);
        this.putShape(style.shape, p.x, h, p.y, p.rot, 0, k, 1, idx);
        glowK = 0.5;
        break;
      }
      case 'grenade':
        this.putShape(style.shape, p.x, h, p.y, p.rot, 0, 1.6 * size * (BILLBOARD[style.shape] ? 0.9 : 1), 1, idx);
        glowK = 1.3;
        break;
      case 'missile': {
        const t = Math.min(1, Math.max(0, p.arcT));
        h = FLIGHT_HEIGHT + 4 * p.arcHeight * t * (1 - t);
        const slope = (4 * p.arcHeight * (1 - 2 * t)) / MISSILE_RUN;
        if (style.shape === 'capsule' || style.shape === 'orb') this.putPool(this.rocket, p.x, h, p.y, p.rot + Math.PI / 2, -Math.atan(slope), 0, 1.3 * size, 1.3 * size, 1.3 * size);
        else this.putShape(style.shape, p.x, h, p.y, p.rot, slope, 1.5 * size, 1, idx);
        glowK = 1.1;
        break;
      }
    }
    if (style.glow) this.putGlow(p.x, h, p.y, glowK * size * (p.crit ? 1.4 : 1));
    if ((style.trail !== 'none' || this.forceTrail) && this.trailSink && this.budget > 0 && (idx + this.tick) % TRAIL_PERIOD[p.kind] === 0) {
      this.budget--;
      this.trailSink(style, p.x, h, -p.y, Math.cos(p.rot), -Math.sin(p.rot));
    }
  }

  end(): void {
    for (const pool of this.pools) {
      pool.mesh.count = pool.n;
      if (pool.hull) pool.hull.count = pool.n;
      const used = pool.n > 0;
      pool.mesh.visible = used;
      if (pool.hull) pool.hull.visible = used;
      if (!used) continue;
      pool.mesh.instanceMatrix.needsUpdate = true;
      if (pool.hull) pool.hull.instanceMatrix.needsUpdate = true;
      if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true;
      if (pool.tint) pool.tint.needsUpdate = true;
    }
    for (let i = this.beamsUsed; i < this.beams.length; i++) {
      const slot = this.beams[i];
      if (slot) {
        slot.mesh.visible = false;
        slot.core.visible = false;
      }
    }
  }

  dispose(): void {
    for (const pool of this.pools) {
      this.scene.remove(pool.mesh);
      pool.mesh.dispose();
      if (pool.hull) {
        this.scene.remove(pool.hull);
        pool.hull.dispose();
      }
    }
    for (const slot of this.beams) this.scene.remove(slot.mesh, slot.core);
    this.beams.length = 0;
    this.trailSink = null;
  }

  // ------------------------------------------------------------------------------------------------------------------

  private makePool(geometry: BufferGeometry, material: Material, cap: number, hullSource: BufferGeometry | null): Pool {
    const tint = new InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    tint.setUsage(DynamicDrawUsage);
    geometry.setAttribute('instanceTint', tint);
    const mesh = new InstancedMesh(geometry, material, cap);
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    mesh.instanceColor = new InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    mesh.instanceColor.setUsage(DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.visible = false;
    const hull = new InstancedMesh(this.kit.hullGeometry(hullSource ?? geometry), this.kit.outline(RENDER.outline.thinPx), cap);
    hull.instanceMatrix.setUsage(DynamicDrawUsage);
    hull.frustumCulled = false;
    hull.count = 0;
    hull.visible = false;
    hull.renderOrder = -1;
    hull.name = 'outline';
    this.scene.add(mesh, hull);
    return { mesh, hull, tint, n: 0 };
  }

  /** Degrade radial blanc (alpha seule) du halo. */
  private glowTexture(): DataTexture {
    const n = 64;
    const data = new Uint8Array(n * n * 4);
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const r = Math.hypot((x + 0.5) / n - 0.5, (y + 0.5) / n - 0.5) * 2;
        const a = r >= 1 ? 0 : (1 - r) * (1 - r) * 0.85;
        const i = (y * n + x) * 4;
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = Math.round(a * 255);
      }
    }
    const tex = new DataTexture(data, n, n, RGBAFormat);
    tex.minFilter = LinearFilter;
    tex.magFilter = LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return tex;
  }

  /** Couleurs de l'instance courante dans cMain / cAcc : crit = blanc, arc-en-ciel = teinte cyclique decalee par projectile. */
  private colorsOf(style: AmmoStyle, crit: boolean, idx: number): void {
    if (style.rainbow) {
      const h = this.time * 0.7 + idx * 0.071;
      rainbowInto(this.cMain, h);
      rainbowInto(this.cAcc, h + 0.18, 0.78);
    } else {
      this.cMain.copy(colorOf(style.colors[0]));
      this.cAcc.copy(colorOf(style.colors[1]));
    }
    if (crit) this.cMain.copy(colorOf(CRIT_COLOR));
  }

  /** Place une forme de skin ; formes plates = face camera orientees selon la direction ecran, les autres selon la direction du monde. */
  private putShape(shape: AmmoShape, x: number, h: number, y: number, rot: number, slope: number, k: number, len: number, idx: number): void {
    const pool = this.shapes[shape];
    if (!BILLBOARD[shape]) {
      const roll = shape === 'diamond' ? this.time * 6 + idx : 0;
      this.putPool(pool, x, h, y, rot + Math.PI / 2, -Math.atan(slope), roll, k, k, k * len);
      return;
    }
    if (pool.n >= pool.mesh.instanceMatrix.count) return;
    let theta: number;
    if (shape === 'star') theta = this.time * 4 + idx * 0.9;
    else {
      this.dir.set(Math.cos(rot), slope, -Math.sin(rot));
      theta = Math.atan2(this.dir.dot(this.up), this.dir.dot(this.right));
    }
    this.roll.setFromAxisAngle(AXIS_Z, theta);
    this.quat.copy(this.camQ).multiply(this.roll);
    if (shape === 'fish' && Math.cos(theta) < 0) this.quat.multiply(FLIP_X);
    const s = k * SHAPE_SCALE[shape] * len;
    this.pos.set(x, h, -y);
    this.scl.set(s, s, s);
    this.commit(pool);
  }

  private putPool(pool: Pool, x: number, h: number, y: number, yaw: number, pitch: number, roll: number, sx: number, sy: number, sz: number): void {
    if (pool.n >= pool.mesh.instanceMatrix.count) return;
    this.pos.set(x, h, -y);
    this.euler.set(pitch, yaw, roll, 'YXZ');
    this.quat.setFromEuler(this.euler);
    this.scl.set(sx, sy, sz);
    this.commit(pool);
  }

  /** Ecrit matrice (pos / quat / scl), couleur et teinte secondaire de l'instance suivante du pool. */
  private commit(pool: Pool): void {
    const i = pool.n++;
    this.matrix.compose(this.pos, this.quat, this.scl);
    pool.mesh.setMatrixAt(i, this.matrix);
    pool.hull?.setMatrixAt(i, this.matrix);
    if (pool.mesh.instanceColor) this.cMain.toArray(pool.mesh.instanceColor.array, i * 3);
    if (pool.tint) this.cAcc.toArray(pool.tint.array, i * 3);
  }

  private putGlow(x: number, h: number, y: number, diameter: number): void {
    const pool = this.glow;
    if (pool.n >= pool.mesh.instanceMatrix.count) return;
    this.pos.set(x + this.fwd.x * GLOW_BEHIND, h + this.fwd.y * GLOW_BEHIND, -y + this.fwd.z * GLOW_BEHIND);
    this.quat.copy(this.camQ);
    this.scl.set(diameter, diameter, 1);
    this.commit(pool);
  }

  private drawBeam(p: ProjectileFrame, style: AmmoStyle): void {
    const slot = this.beams[this.beamsUsed];
    if (!slot) return;
    this.beamsUsed++;
    const dx = p.x1 - p.x;
    const dy = p.y1 - p.y;
    const len = Math.max(0.01, Math.hypot(dx, dy));
    const w = Math.max(0.05, p.width);
    const haloK = style.glow ? 1.15 : 0.8;
    const coreK = style.glow ? 0.4 : 0.3;
    const yaw = Math.atan2(dy, dx) + Math.PI / 2;
    for (const [m, k] of [[slot.mesh, haloK], [slot.core, coreK]] as const) {
      m.visible = true;
      m.position.set((p.x + p.x1) / 2, FLIGHT_HEIGHT, -(p.y + p.y1) / 2);
      m.rotation.set(0, yaw, 0);
      m.scale.set(w * k, w * k, len);
    }
    const fade = Math.min(1, Math.max(0, p.fade));
    slot.mesh.material = style.rainbow ? slot.rainbow : slot.material;
    slot.material.color.copy(colorOf(style.colors[0]));
    slot.coreMaterial.color.copy(colorOf(style.colors[1]));
    slot.rainbowUniforms.uLen.value = len;
    slot.rainbowUniforms.uTime.value = this.time;
    slot.coreMaterial.opacity = fade;
    slot.material.opacity = fade;
    slot.rainbow.opacity = fade;
  }
}
